import { ShieldCheck, Leaf, HeartHandshake, Sparkles } from "lucide-react";
import PageHero from "./PageHero";
import CtaBand from "./CtaBand";
import { Reveal, SectionLabel } from "./Reveal";
import { STATS } from "../data";
import { useSeo } from "../hooks/useSeo";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Veilig & zonder schade",
    text: "Wij werken uitsluitend met lage druk. Zo reinigen we elke ondergrond grondig zonder het materiaal te beschadigen.",
  },
  {
    icon: Leaf,
    title: "Duurzaam & milieubewust",
    text: "Onze reinigingsmiddelen zijn 100% biologisch afbreekbaar en veilig voor planten, dieren en de omgeving.",
  },
  {
    icon: HeartHandshake,
    title: "Persoonlijk & eerlijk",
    text: "Een duidelijke offerte, een gratis proefstuk en heldere communicatie. Je weet altijd vooraf waar je aan toe bent.",
  },
  {
    icon: Sparkles,
    title: "Vakkundig resultaat",
    text: "We pakken vuil bij de kern aan, zodat je pand niet alleen schoon oogt, maar ook langdurig schoon blijft.",
  },
];

export const OverPage = () => {
  useSeo({
    title: "Over SoftPro | Specialist in softwash reiniging",
    description:
      "Maak kennis met SoftPro: specialist in het reinigen van daken, gevels, terrassen en bedrijfspanden met de zachte softwash-methode. Veilig en duurzaam.",
  });

  return (
    <div data-testid="over-page">
      <PageHero
        title="Over SoftPro"
        subtitle="Wij zijn specialist in het reinigen van daken, gevels, terrassen en bedrijfspanden met de zachte softwash-methode. Veilig, duurzaam en met oog voor een blijvend resultaat."
        image="https://images.pexels.com/photos/1535086/pexels-photo-1535086.jpeg?auto=compress&cs=tinysrgb&w=1920"
        crumb="Over SoftPro"
      />

      {/* Story */}
      <section className="bg-background py-24 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionLabel>Ons verhaal</SectionLabel>
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
              Schoon, veilig en duurzaam — dat is waar SoftPro voor staat
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              SoftPro is ontstaan uit een simpele overtuiging: een pand
              reinigen kan én moet zonder schade. Waar traditionele
              hogedrukreiniging het oppervlak vaak beschadigt en poreus maakt,
              kiezen wij bewust voor de zachte softwash-methode. Met lage druk
              en een biologisch afbreekbaar reinigingsmiddel pakken we vuil,
              algen, mos en schimmels aan tot in de kern.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Of het nu gaat om een bakstenen gevel, een mosbedekt dak, een
              vergroend terras of een compleet bedrijfspand: we behandelen elk
              project met dezelfde vakkundigheid en aandacht. Vooraf een
              duidelijke offerte, een gratis proefstuk zodat je precies weet wat
              je kunt verwachten, en een resultaat dat langdurig mooi blijft.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-secondary py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <SectionLabel>Waar wij voor staan</SectionLabel>
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink max-w-2xl leading-tight">
              Onze belofte bij elk project
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div
                  data-testid={`over-value-${i}`}
                  className="h-full rounded-3xl border border-border bg-card p-7"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-teal/10 text-teal">
                    <v.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-ink">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {v.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-navy-deep grain py-20 sm:py-24" data-testid="over-stats">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="text-center sm:text-left">
                  <p className="font-display text-4xl sm:text-5xl font-bold text-white">
                    {s.value}
                    <span className="text-teal">{s.suffix}</span>
                  </p>
                  <p className="mt-2 text-sm text-white/60">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export default OverPage;
